import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaIconComponent } from 'ra-ui';
import { LinkController } from 'src/app/domain/controllers/link.controller';
import { LinkEntity } from 'src/app/domain/entities/link.entitty';
import { RaTooltipDirective } from 'src/app/shared/directives/ra-tooltip/ra-tooltip.directive';
import { DATA_TAG_CONTEXT } from '../ra-editor/data-tag-context.token';
import { getDataTagLabel } from '../ra-editor/data-tag-i18n';
import { getLinkCategoryIcon } from '../ra-editor/link-category-icon';
import { isDataTagFilled } from '../ra-editor/data-tag-value.resolver';

/** Valor emitido pelo composer: token escolhido OU texto livre (mutuamente exclusivos). */
export interface LinkTokenComposerValue {
    link?: LinkEntity;
    text?: string;
}

/**
 * Input single-line que aceita texto livre OU um token de dado (`$COMPANY_EMAIL`, …)
 * escolhido via autocomplete ao digitar `@`. Cada campo é um token (chip) XOR texto.
 * Reusa a lista de links (`LinkController`) e os rótulos i18n do RaEditor.
 */
@Component({
    selector: 'ra-link-token-composer',
    templateUrl: 'ra-link-token-composer.component.html',
    styleUrl: 'ra-link-token-composer.component.scss',
    imports: [CommonModule, TranslatePipe, RaIconComponent, RaTooltipDirective],
})
export class RaLinkTokenComposerComponent implements OnInit {
    private _translate = inject(TranslateService);
    private _linkController = inject(LinkController);
    private _dataTagContext = inject(DATA_TAG_CONTEXT, { optional: true });
    private _host = inject(ElementRef);

    public text = input<string>('');
    public linkTagName = input<string>('');
    public limitChar = input<number | undefined>(undefined);
    public placeholder = input<string>('');
    public disabled = input<boolean>(false);

    public valueChange = output<LinkTokenComposerValue>();

    /** Texto em edição no modo livre — fonte da verdade do input. */
    public draft = signal<string>('');

    // * [Autocomplete]
    public showAutocomplete = signal(false);
    public autocompleteSearch = signal('');
    /** -1 = nenhum item focado; a 1ª seta pra baixo foca o primeiro */
    public autocompleteIndex = signal(-1);

    /** Modo token quando há `linkTagName` ou o texto é um `$TOKEN`. */
    public isToken = computed(() => !!this.linkTagName() || (this.text() ?? '').startsWith('$'));

    public chipLabel = computed(() =>
        getDataTagLabel(this._translate, this.linkTagName() || this.text())
    );

    /**
     * Token sem dado no contexto atual → chip vermelho + tooltip "campo não preenchido".
     * Mesmo critério do RaEditor (`isDataTagFilled`): sem contexto, assume preenchido.
     */
    public isEmpty = computed(() => {
        if (!this.isToken()) return false;
        const tag = this.linkTagName() || this.text();
        return !isDataTagFilled(tag, this._dataTagContext?.() ?? null);
    });

    /** Lista plana filtrada pela busca (após o `@`). */
    private _filteredLinks = computed(() => {
        const search = this.autocompleteSearch().toUpperCase().trim();
        const links = this._linkController.links();
        if (!search) return links;
        const searchNoDollar = search.replace(/^\$/, '');
        return links.filter(link => {
            const fields = [
                this._normalize(link.value ?? link.tagName ?? '').toUpperCase(),
                (link.tagName ?? '').toUpperCase(),
                (link.name ?? '').toUpperCase(),
                (link.description ?? '').toUpperCase(),
                this.getLinkLabel(link).toUpperCase(),
                (link.categoryDescription ?? '').toUpperCase(),
            ];
            return fields.some(v => v.includes(search) || v.includes(searchNoDollar));
        });
    });

    /**
     * Links agrupados por categoria. O `flatIndex` é numerado DEPOIS da ordenação
     * dos grupos: a navegação por teclado precisa seguir a ordem da tela, não a
     * ordem em que a API devolveu os links.
     */
    public filteredLinksByCategory = computed(() => {
        const links = this._filteredLinks();
        const groups = new Map<string, LinkEntity[]>();
        for (const link of links) {
            const cat = this._categoryLabel(link);
            if (!groups.has(cat)) groups.set(cat, []);
            groups.get(cat)!.push(link);
        }
        let flatIndex = 0;
        return Array.from(groups.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([categoryDescription, links]) => ({
                categoryDescription,
                items: links.map(link => ({ link, flatIndex: flatIndex++ })),
            }));
    });

    /** Lista achatada na ordem exibida — base da navegação por teclado. */
    public displayLinks = computed(() =>
        this.filteredLinksByCategory().flatMap(group => group.items.map(item => item.link))
    );

    ngOnInit(): void {
        if (!this.isToken()) this.draft.set(this.text() ?? '');
        if (this._linkController.links().length === 0) {
            this._linkController.getAll().subscribe();
        }
    }

    /** Rótulo traduzido do link para a lista de autocomplete. */
    public getLinkLabel(link: LinkEntity): string {
        const tag = this._normalize(link.value ?? link.tagName ?? link.name ?? '');
        return getDataTagLabel(this._translate, tag, link.description || link.tagName || link.name);
    }

    /** Ícone da categoria do link, exibido na lista de autocomplete. */
    public getLinkIcon(link: LinkEntity): string {
        return getLinkCategoryIcon(link);
    }

    public onInput(event: Event): void {
        const el = event.target as HTMLInputElement;
        const value = el.value;
        this.draft.set(value);
        this.valueChange.emit({ text: value });

        const caret = el.selectionStart ?? value.length;
        const match = value.slice(0, caret).match(/@([^\s@]*)$/);
        if (match) {
            this.autocompleteSearch.set(match[1]);
            this.autocompleteIndex.set(-1);
            this.showAutocomplete.set(true);
        } else {
            this._closeAutocomplete();
        }
    }

    public onKeydown(event: KeyboardEvent): void {
        if (!this.showAutocomplete()) return;
        const list = this.displayLinks();
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                // Nada selecionado ainda: a primeira seta pra baixo foca o 1º item
                this.autocompleteIndex.set(
                    Math.min(this.autocompleteIndex() + 1, Math.max(0, list.length - 1))
                );
                setTimeout(() => this._scrollSelectedIntoView(), 0);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.autocompleteIndex.set(Math.max(this.autocompleteIndex() - 1, 0));
                setTimeout(() => this._scrollSelectedIntoView(), 0);
                break;
            case 'Enter': {
                event.preventDefault();
                // Sem item focado ainda, Enter confirma o primeiro da lista
                const link = list[Math.max(this.autocompleteIndex(), 0)];
                if (link) this.selectLink(link);
                break;
            }
            case 'Escape':
                event.preventDefault();
                this._closeAutocomplete();
                break;
        }
    }

    /** Atraso curto para permitir o `mousedown` nos itens antes de fechar. */
    public onBlur(): void {
        setTimeout(() => this._closeAutocomplete(), 150);
    }

    public selectLink(link: LinkEntity): void {
        this._closeAutocomplete();
        this.valueChange.emit({ link });
    }

    public removeToken(): void {
        this.draft.set('');
        this.valueChange.emit({ text: '' });
    }

    private _closeAutocomplete(): void {
        this.showAutocomplete.set(false);
        this.autocompleteSearch.set('');
        this.autocompleteIndex.set(-1);
    }

    /**
     * Rola apenas o container da lista (não a página) até o item selecionado,
     * sem animação: com a seta pressionada, animações concorrentes se atrapalham.
     */
    private _scrollSelectedIntoView(): void {
        const host = this._host.nativeElement as HTMLElement;
        const list = host.querySelector<HTMLElement>('.autocomplete-list');
        const selected = host.querySelector<HTMLElement>('.autocomplete-item.selected');
        if (!list || !selected) return;

        const itemTop = selected.offsetTop;
        const itemBottom = itemTop + selected.offsetHeight;
        const viewTop = list.scrollTop;
        const viewBottom = viewTop + list.clientHeight;

        if (itemTop < viewTop) {
            list.scrollTop = itemTop;
        } else if (itemBottom > viewBottom) {
            list.scrollTop = itemBottom - list.clientHeight;
        }
    }

    private _normalize(v: string): string {
        return v.replace(/^\$/, '').trim();
    }

    private _categoryLabel(link: LinkEntity): string {
        if (link.categoryDescription?.trim()) return link.categoryDescription;
        const code = link.category?.trim();
        if (!code) return this._translate.instant('shared.editor.categoryOther');
        const key = `shared.editor.linkCategories.${code}`;
        const translated = this._translate.instant(key);
        return translated !== key ? translated : code;
    }
}
